import React from 'react';
import loadable from '@loadable/component';

import { doSearch } from '../search-filter/search-filter-actions';
import withPage from '../page';
import { SwiftypeAPI } from '../core/src/swiftype-api-client';
import { fetchSlugInfo } from '../page/page-actions';
import { ssrRelatedPages } from '../related-pages/related-pages-actions';
import { DEFAULT_SEARCH_RESULTS } from '../search-filter/search-filter-reducer';

import dlpToProductCategoriesMap from './dlp-product-categories-map';

const DLP = loadable(() => import('./components/dlp'));
const DLPV2 = loadable(() => import('./components-v2/dlp'));

export const DLP_DEFAULT_SEARCH_OPTIONS = {
    pageSize: 8,
    resultsLimit: 8,
    filters: { instock: 'true' },
};

const getDLPKeyword = slugInfo => {
    const { components = {} } = slugInfo || {};
    const { dlpKeyword: { keyword } = {} } = components;
    return keyword || slugInfo.key;
};

const getDLPFilters = (slugInfo, query) => {
    const { categories } = slugInfo;
    const { pStoreID } = query || {};
    let searchFilter = {
        filters: {
            all: [{ ...DLP_DEFAULT_SEARCH_OPTIONS.filters }],
        },
    };

    if (!pStoreID) {
        searchFilter.filters.all.push({ private_store_only: 'false' });
    } else if (pStoreID.length > 0) {
        searchFilter.filters.all.push({ private_stores: pStoreID });
    }

    if (!categories) return searchFilter;

    try {
        if (dlpToProductCategoriesMap[categories[0]] && dlpToProductCategoriesMap[categories[0]].length > 0) {
            searchFilter.filters.all.push({ categories: dlpToProductCategoriesMap[categories[0]] });
        }
    } catch (e) {
        console.log('error', e);
    }
    return searchFilter;
};

/**
 * Transforms weird Swiftype response into a much more usable format.
 *    Example response:
 *       { price: { raw: 500.00 }, name: { raw: "Product Name" } }
 *    Would become:
 *       { price: 500.00, name: "Product Name" }
 *
 * Formats the response object in appropriate way.
 */
export const getProducts = (query, aggregations, sort, startPage, pageSize, keyword, filters, fetchAll) => {
    const fields = ['facets', 'images', 'offers', 'price', 'specs', 'rank', 'reviews'];
    return new SwiftypeAPI()
        .getProducts(keyword, {
            ...query,
            page: {
                size: fetchAll ? DLP_DEFAULT_SEARCH_OPTIONS.resultsLimit : pageSize,
                current: fetchAll ? 1 : startPage / pageSize + 1,
            },
            sort: Object.keys(sort).length === 0 ? undefined : sort,
        })
        .then(resultList => {
            const { info, results } = resultList;

            const parsedList = results.reduce((newResults, prod, index) => {
                const transformedProd = {
                    //add organic sort order from results
                    organic: index,
                };

                for (let key in prod.data) {
                    const transformedKey = key
                        .split('_')
                        .map((elem, i) => {
                            if (i !== 0) {
                                let arr = elem.split('');
                                arr[0] = arr[0].toUpperCase();
                                return arr.join('');
                            } else {
                                return elem;
                            }
                        })
                        .join('');

                    transformedProd[transformedKey] = prod.getRaw(key);
                    if (fields.indexOf(transformedKey) > -1 && transformedProd[transformedKey]) {
                        if (transformedProd[transformedKey].constructor === Array) {
                            transformedProd[transformedKey] = transformedProd[transformedKey].map(elem =>
                                JSON.parse(elem)
                            );
                        } else {
                            transformedProd[transformedKey] = JSON.parse(transformedProd[transformedKey]);
                        }
                    }

                    // TEMP FIX
                    if (transformedKey === 'linkUrl') {
                        transformedProd.slug = prod.getRaw(key).split('/').pop();
                    }
                }

                newResults.push(transformedProd);
                return newResults;
            }, []);

            return {
                data: {
                    hits: {
                        hits: parsedList,
                        total: info.meta.page.total_results,
                    },
                },
            };
        });
};

const loadData = async (urlParams, store, context) => {
    const { query: queryObj } = context;
    const { slug } = urlParams;
    const fullSlug = `/dlp/${slug}`;

    await store.dispatch(fetchSlugInfo(fullSlug, queryObj));

    const { slugInfo, redirects, basename } = store.getState();

    const searchKeyword = getDLPKeyword(slugInfo);

    const needRedirect = !searchKeyword || !slugInfo || slugInfo.error || (redirects && redirects.count !== 0);

    const { destination = `${basename}/slp/weekly-deals`, status = 301 } = redirects;
    if (needRedirect) return Promise.resolve({ status, destination });

    const { aggregation, sort } = DEFAULT_SEARCH_RESULTS;
    const { pageSize } = DLP_DEFAULT_SEARCH_OPTIONS;
    const startPage = 0;

    // query should look like this eventually:
    // { query: <DLPKeyword>, filters: { all: [<filters as objects>, {categories: <Product category linked to dlp category>}], ... } }
    const query = {
        query: searchKeyword,
        ...getDLPFilters(slugInfo, queryObj),
    };

    return Promise.all([
        store.dispatch(ssrRelatedPages(`${slug}:relatedPages`, slugInfo)),
        store.dispatch(
            doSearch(searchKeyword, () =>
                getProducts(query, aggregation, sort, startPage, pageSize, searchKeyword, null, true)
            )
        ),
    ]);
};

const dlpContainer = props => {
    const { slugInfo, search } = props;
    const { templateKey } = slugInfo || {};
    const { aggregation, sort } = DEFAULT_SEARCH_RESULTS;
    const { pageSize } = DLP_DEFAULT_SEARCH_OPTIONS;
    const startPage = 0;
    const searchKeyword = getDLPKeyword(slugInfo) || 'csr-fetching';

    const searchParams = {
        query: searchKeyword,
        ...getDLPFilters(slugInfo, search),
    };

    const compProps = {
        searchKey: searchKeyword,
        searchFunc: () => getProducts(searchParams, aggregation, sort, startPage, pageSize, searchKeyword, null, true),
        ...props,
    };

    return templateKey === 'dlp-v2' ? <DLPV2 {...compProps} /> : <DLP {...compProps} />;
};

export default {
    component: withPage(dlpContainer, { wrapperClassName: 'dlp-root', disableMobileBreadcrumb: true }),
    loadData: loadData,
};
